import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

import { palette } from 'theme/palette';
import { shadows } from 'theme/shadows';
import { typography } from 'theme/typography';
import { customShadows } from 'theme/custom-shadows';
import { createPresets } from 'theme/presets';
import { componentsOverrides } from 'theme/overrides';

function getColorBrightness(hexColor) {
  const hex = hexColor.replace(/^#/, '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return brightness;
}

// ----------------------------------------------------------------------

const extractFontFamily = font => {
  if (!font) return null;

  const fontFamilyMatch = font.match(/['"]([^'"]+)['"]/);
  if (fontFamilyMatch) {
    return fontFamilyMatch[0];
  }

  return null;
};

const cleanFontFamilyForUrl = fontFamily => {
  if (!fontFamily) return null;

  const cleanedFont = fontFamily
    .replace(/'/g, '')
    .replace(/"/g, '')
    .split(',')[0]
    .trim();

  return cleanedFont.replace(/\s+/g, '+');
};

const loadGoogleFont = fontFamily => {
  if (!fontFamily) return;

  const cleanedFont = cleanFontFamilyForUrl(fontFamily);
  if (!cleanedFont) return;

  const fontUrl = `https://fonts.googleapis.com/css2?family=${cleanedFont}&display=swap`;

  const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
  if (existingLink) return;

  const link = document.createElement('link');
  link.href = fontUrl;
  link.rel = 'stylesheet';
  document.head.appendChild(link);
};

export default function ThemeProvider({ children, menuTheme }) {
  const themeMode = menuTheme?.text
    ? getColorBrightness(menuTheme.text) > 0.5
      ? 'dark'
      : 'light'
    : 'dark';
  const themeDirection = 'ltr';
  const themeColorPresets = 'preset03';

  const presets = createPresets(themeColorPresets);
  const defaultPalette = palette(themeMode);
  const defaultTypography = typography;

  const fontFamily = extractFontFamily(menuTheme.font);

  useEffect(() => {
    if (fontFamily) {
      loadGoogleFont(fontFamily);
    }
  }, [fontFamily]);

  const themeConfig = {
    palette: {
      ...defaultPalette,
      ...presets.palette,
      ...(menuTheme && {
        primary: {
          main: menuTheme.main || defaultPalette.primary.main,
        },
        background: {
          default: menuTheme.background || defaultPalette.background.default,
          paper: menuTheme.tabBackground || defaultPalette.background.paper,
        },
        text: {
          primary: menuTheme.text || defaultPalette.text.primary,
          secondary: menuTheme.tabText || defaultPalette.text.secondary,
        },
      }),
    },
    customShadows: {
      ...customShadows(themeMode),
      ...presets.customShadows,
    },
    direction: themeDirection,
    shadows: shadows(themeMode),
    shape: { borderRadius: 8 },
    typography: {
      ...defaultTypography,
      fontFamily,
      h1: {
        ...defaultTypography.h1,
        fontFamily,
      },
      h2: {
        ...defaultTypography.h2,
        fontFamily,
      },
      h3: {
        ...defaultTypography.h3,
        fontFamily,
      },
      h4: {
        ...defaultTypography.h4,
        fontFamily,
      },
      h5: {
        ...defaultTypography.h5,
        fontFamily,
      },
      h6: {
        ...defaultTypography.h6,
        fontFamily,
      },
    },
  };

  console.log(themeConfig);

  const theme = createTheme(themeConfig);

  theme.components = componentsOverrides(theme);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
}

ThemeProvider.propTypes = {
  children: PropTypes.node,
  menuTheme: PropTypes.object,
};
